import { useFragment } from "@/generated";
import { withToast } from "@/lib/utils/withToast";
import type { Reference } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { MoveProjectsToTrashDocument, ProjectFieldsFragmentDoc } from "../../../generated/graphql";

/**
 * Hook for moving projects to trash (soft delete).
 *
 * Provides a mutation function to mark one or more projects as trashed. Projects are not
 * permanently deleted and can be restored later. Automatically refetches root
 * and parent project queries to update the UI.
 *
 * @returns An object containing:
 *   - moveProjectsToTrash: Mutation function to trash projects
 *   - loading: Boolean indicating if the mutation is in progress
 *   - error: ApolloError if the mutation failed, undefined otherwise
 *
 * @example
 * const { moveProjectsToTrash, loading, error } = useMoveProjectToTrash();
 * // Move single project
 * await moveProjectsToTrash({ variables: { ids: ["project123"] } });
 * // Move multiple projects
 * await moveProjectsToTrash({ variables: { ids: ["project1", "project2", "project3"] } });
 */
export function useMoveProjectsToTrash() {
  const [moveProjectsToTrashMutation, { loading, error }] = useMutation(
    MoveProjectsToTrashDocument,
    {
      update(cache, { data }) {
        if (!data?.moveProjectsToTrash) return;

        try {
          const projects = data.moveProjectsToTrash.map((project) =>
            useFragment(ProjectFieldsFragmentDoc, project)
          );

          const projectsByParentIdMap = new Map<string | null, typeof projects>();

          for (const project of projects) {
            const key = project.parentId ?? null;

            const projectsByParent = projectsByParentIdMap.get(key) ?? [];
            projectsByParent.push(project);
            projectsByParentIdMap.set(key, projectsByParent);
          }

          cache.batch({
            update(cache) {
              for (const project of projects) {
                const id = cache.identify(project);
                if (id) {
                  cache.writeFragment({
                    id,
                    fragment: ProjectFieldsFragmentDoc,
                    data: project,
                  });
                }

                // Update project isTrash => true
                cache.modify({
                  id: `Project:${project.id}`,
                  fields: {
                    isTrash() {
                      return true;
                    },
                  },
                });
              }

              for (const [parentId, projects] of projectsByParentIdMap.entries()) {
                const newRefs = projects
                  .map((p) => cache.identify(p))
                  .filter(Boolean)
                  .map((id) => ({ __ref: id }));

                // Add project to trash page
                cache.modify({
                  id: "ROOT_QUERY",
                  fields: {
                    projectsByParentId(existingData, { storeFieldName }) {
                      const key =
                        parentId === null ? `"parentId":null` : `"parentId":"${parentId}"`;
                      const isTrashKey = '"isTrash":true';

                      if (!(storeFieldName.includes(key) && storeFieldName.includes(isTrashKey)))
                        return existingData;
                      if (!existingData?.items) return existingData;

                      return {
                        ...existingData,
                        items: [...newRefs, ...existingData.items],
                        total: (existingData.total || 0) + newRefs.length,
                      };
                    },
                  },
                });

                // Substracts parent itemCount
                if (parentId !== null) {
                  cache.modify({
                    id: `Project:${parentId}`,
                    fields: {
                      itemCount(existingData = 0) {
                        return existingData - newRefs.length;
                      },

                      trashCount(existingData = 0) {
                        return existingData + newRefs.length;
                      },
                    },
                  });
                }

                // Remove project from main page
                cache.modify({
                  id: "ROOT_QUERY",
                  fields: {
                    projectsByParentId(existingData, { storeFieldName, readField }) {
                      const key =
                        parentId === null ? `"parentId":null` : `"parentId":"${parentId}"`;
                      const isTrashKey = '"isTrash":false';

                      if (!(storeFieldName.includes(key) && storeFieldName.includes(isTrashKey)))
                        return existingData;
                      if (!existingData?.items) return existingData;

                      return {
                        ...existingData,
                        items: existingData.items.filter(
                          (ref: Reference) => !projects.some((p) => readField("id", ref) === p.id)
                        ),
                        total: (existingData.total || 0) - newRefs.length,
                      };
                    },
                  },
                });

                // Increment Trash count number
                if (parentId === null) {
                  cache.modify({
                    id: "ROOT_QUERY",
                    fields: {
                      rootTrashCount(existingData) {
                        return existingData + newRefs.length;
                      },
                    },
                  });
                }
              }
            },
          });
        } catch (error) {
          console.error("Move projects to trash cache update failed:", error);
        }
      },
    }
  );

  const moveProjectsToTrash = async (projectIds: string[], skipCheck: boolean) => {
    return withToast(
      async () => {
        const result = await moveProjectsToTrashMutation({
          variables: {
            ids: projectIds,
            skipCheck,
          },
        });

        const maskedProjects = result.data?.moveProjectsToTrash ?? [];
        const trashedProjects = maskedProjects.map((project) =>
          useFragment(ProjectFieldsFragmentDoc, project)
        );

        return trashedProjects;
      },
      {
        loading: `Moving ${projectIds.length} project${projectIds.length !== 1 ? "s" : ""} to trash...`,
        success: `Successfully moved ${projectIds.length} project${projectIds.length !== 1 ? "s" : ""} to trash`,
        error: "Failed to move projects to trash. Please try again.",
      },
      "moveProjectsToTrash"
    );
  };

  return { moveProjectsToTrash, loading, error };
}
